/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Icons,
  MRT_RowSelectionState as RowSelectionState,
  MRT_SortingState,
  useMaterialReactTable,
  MRT_ColumnFiltersState,
  MRT_VisibilityState,
  MRT_ColumnPinningState,
} from 'material-react-table';
import { Updater } from '@tanstack/react-table';
import { Box, Modal, Typography, Divider, List, MenuItem, ListItemText, IconButton } from '@mui/material';
import { t } from 'i18next';
import { isEmpty } from 'lodash';

import { getShowActionsColumn } from '../../../Utilities/utils';
import { StyleConstants } from '../../../Constants/Style.Constants';

import { IUpcomingShift } from '../../../../Modules/Jobs/Components/UpcomingShifts/UpcomingShifts.Model';
import { DropdownOptions } from '../../../../Modules/Vendor/Components/AllSchedules/AllSchedules.Model';
import { JobManagementType, JobRow } from '../../../../Modules/Vendor/Components/AllJobs/AllJobs.Model';
import { IProcessEvents } from '../../../../routes/config';

import { TaskData } from '../../../../Modules/Jobs/Components/WorkTasks/WorkTasks';
import { IJobOverview } from '../../../../Modules/Jobs/Components/JobsListView/JobsListView';
import { IJobs } from '../../../../Modules/Jobs/Components/JobView/JobsView';
import { IBidList } from '../../../../Modules/Jobs/Components/JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../../Modules/Customer/Components/ViewFacilityDetails/ViewFacilityDetails';
import Button, { ButtonType } from '../Buttons/Button';
import DropdownWithButton from '../../../../Modules/Vendor/Components/AllSchedules/AllJobsModifyDropDown';
import { FiltersState } from '../../../Store/features/filtersSlice';
import ModifyPopUp from '../../../../Modules/Jobs/Components/JobDetails/ModifyPopUp';
import {
  clearSelection,
  removeSelectedJobsState,
  setRowSelectionState,
  setSelectedRowDataMapState,
} from '../../../Store/features/jobSelectionSlice';
import { RootState } from '../../../Store/store';

import theme from '../../../Themes/theme';
import { Icons } from '../../../Constants/Icons';
import { dropdownStyles } from '../MultiSelectDropdownWithCheckboxes/MultiSelectDropdownWithCheckboxes.Style';
import { ActionsColumn, configureColumnStyles, RowSelectionCountDiv, SelectAllButtonDiv } from './DataGrid.styles';
import {
  ButtonContainer,
  ModifyButtonContainer,
} from '../../../../Modules/Vendor/Components/AllSchedules/AllJobs.Style';
import WhiteTooltip from '../CommonStyle/Tooltip.Style';

interface IDataGridProps {
  vendorTypes?: any;
  columns: MRT_ColumnDef<TaskData | IJobOverview | IJobs | IBidList | IProfileCardProps>[];
  data: TaskData[] | IJobOverview[] | IJobs[] | IBidList[] | IProfileCardProps[] | IUpcomingShift[];
  onRowChange?: (rowSelection: object, data?: TaskData[] | IJobOverview[]) => void;
  enableRowSelect?: boolean;
  enableColumnPinning?: boolean;
  expanded?: boolean | number;
  leftPinArr: string[];
  rightPinArr: string[];
  enableEditing?: boolean;
  sortingObj?: MRT_SortingState;
  sort?: (sorting: any) => void;
  filter?: (sorting: any) => void;
  sortingFns?: any;
  loader?: boolean;
  errorMessageTitle?: string;
  errorMessageDescription?: string;
  handleSaveCell?: (cell: any, value: string) => void;
  editingMode: 'cell' | 'table' | 'modal' | 'row' | undefined;
  jobType?: string;
  configureColumns?: boolean;
  onMultipleRowsSelected?: boolean;
  selectActions?: boolean;
  setSelectedRows?: (data: any[]) => void;
  selectedRows?: any[];
  isRescheduleShift?: boolean;
  disableDifferentDateRows?: boolean;
  type?: string;
  setSelectedDropDownValue?: (dropdownOptions: DropdownOptions) => void;
  selectedDropDownValue?: DropdownOptions | '';
  totalCount?: number;
  isReassigning?: boolean;
  unSelectJobIds?: any[];
  removeSelectedJobs?: boolean;
  filters?: FiltersState;
  onFetchAllRows?: () => Promise<JobRow[]>;
  showSelectAllButton?: boolean;
  resetSelectionSignal?: number;
  storeRowSelection?: boolean;
  fetchData?: () => void;
  processEvents?: IProcessEvents[];
  canShowActionsForJobs?: boolean;
  canShowActionsForSchedules?: boolean;
  showScWorkOrderIdColumn?: boolean;
  columnFilterDisplayMode?: 'custom' | 'popover' | 'subheader';
}

const CustomIcons: Partial<MRT_Icons> = {
  MoreVertIcon: () => <img src={Icons.SortingIcon} alt={t('altTexts:more')} />,
};

/**
 *
 * @returns DataGrid for Given Data
 */
const DataGrid = ({
  columns,
  data,
  onRowChange,
  enableEditing,
  sort,
  filter,
  loader,
  editingMode,
  configureColumns,
  enableRowSelect,
  selectActions,
  setSelectedRows,
  selectedRows,
  isRescheduleShift,
  disableDifferentDateRows,
  type,
  setSelectedDropDownValue,
  selectedDropDownValue,
  totalCount,
  unSelectJobIds,
  removeSelectedJobs,
  filters,
  onFetchAllRows,
  showSelectAllButton,
  resetSelectionSignal,
  storeRowSelection,
  enableColumnPinning,
  fetchData,
  processEvents,
  canShowActionsForJobs,
  canShowActionsForSchedules,
  showScWorkOrderIdColumn,
  columnFilterDisplayMode,
}: IDataGridProps) => {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const [selectedRowDataMap, setSelectedRowDataMap] = useState<Record<string, any>>({});

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({
    jobName: true,
    location: true,
    jobStatus: true,
    createdOn: true,
    scWorkOrderId: showScWorkOrderIdColumn ?? false,
    Actions: getShowActionsColumn(type, canShowActionsForJobs, canShowActionsForSchedules),
  });
  const [columnPinning, setColumnPinning] = useState<MRT_ColumnPinningState>({
    left: [],
    right: [],
  });

  const [showConfig, setShowConfig] = useState(false);
  const [isOpenSelectAll, setIsOpenSelectAll] = useState(false);
  const [isLoadingSelectAll, setIsLoadingSelectAll] = useState(false);
  const [isAllRowsSelected, setIsAllRowsSelected] = useState(false);
  const hasMounted = useRef(false);
  const { checkBox, listItemStyles } = dropdownStyles;
  const { columnsVisible } = configureColumnStyles;

  const dispatch = useDispatch();
  const rowSelectionState = useSelector((state: RootState) => state.jobSelection.rowSelection);
  const selectedRowDataMapState = useSelector((state: RootState) => state.jobSelection.selectedRowDataMap);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    setRowSelection((prev) => {
      const newSelection = typeof updater === 'function' ? updater(prev) : updater;

      // Update selectedRowDataMap
      const updatedDataMap = { ...selectedRowDataMap };
      data.forEach((row) => {
        const rowId =
          type === JobManagementType.AllSchedules
            ? isRescheduleShift
              ? row.workOrderScheduleId
              : row.woScheduleMasterId
            : row.jobId;
        const isSelected = newSelection[rowId as string];
        if (isSelected) {
          updatedDataMap[rowId as string] = row;
        } else {
          delete updatedDataMap[rowId as string];
        }
      });

      setSelectedRowDataMap(updatedDataMap);

      if (storeRowSelection) {
        dispatch(setRowSelectionState(newSelection));
        dispatch(setSelectedRowDataMapState(updatedDataMap));
      }
      return newSelection;
    });
  };

  const isMultipleRowsSelected = useMemo(() => {
    return Object.keys(rowSelection)?.length > 0;
  }, [rowSelection]);

  const handleShowConfig = useCallback(() => {
    setShowConfig(true);
  }, []);

  const handleOpenSelectAll = () => {
    setIsOpenSelectAll(true);
  };

  const handleCloseSelectAll = () => {
    setIsOpenSelectAll(false);
  };

  const handleSelectAll = async () => {
    if (!onFetchAllRows) return;

    try {
      setIsLoadingSelectAll(true);
      const allData: JobRow[] = await onFetchAllRows();
      const newSelection: Record<string, boolean> = {};
      const newSelectedRowDataMap: Record<string, any> = {};
      allData.forEach((row) => {
        const rowId =
          type === JobManagementType.AllSchedules
            ? isRescheduleShift
              ? row.workOrderScheduleId
              : row.woScheduleMasterId
            : row.jobId;
        if (rowId) {
          newSelection[rowId] = true;
          newSelectedRowDataMap[rowId] = row;
        }
      });

      setRowSelection(newSelection);
      setSelectedRowDataMap(newSelectedRowDataMap);
      setIsLoadingSelectAll(false);
      setIsOpenSelectAll(false);
    } catch (error) {
      console.error(t('jobManagement:failedToSelectAllRows'), error);
    }
  };

  const handleRemoveSelection = () => {
    dispatch(clearSelection());
    setRowSelection({});
    setSelectedRowDataMap({});
    setIsOpenSelectAll(false);
  };

  const handleEnableRowSelection = (original?: TaskData | IJobOverview | IJobs | IBidList | IProfileCardProps) => {
    if (!enableRowSelect) return false;

    if (disableDifferentDateRows) {
      const selectedRowIds = table?.getState()?.rowSelection;
      const selectedRows = table?.getPrePaginationRowModel()?.rows?.filter((r) => selectedRowIds[r?.id]);

      if (selectedRows?.length > 0) {
        const sameWorkOrderScheduleMasterSelections = selectedRows?.filter(
          (r) => r?.original?.woScheduleMasterId === original?.woScheduleMasterId
        );
        if (sameWorkOrderScheduleMasterSelections?.length > 0) {
          const firstSelected = sameWorkOrderScheduleMasterSelections?.[0]?.original;

          const isSameDate = original?.scheduleDate === firstSelected?.scheduleDate;

          if (!isSameDate) return false;
        }
      }
      return !original?.isRescheduled && original?.dataStatus === 'A';
    }
    return true;
  };

  const table = useMaterialReactTable({
    columns,
    data,
    // enableRowSelection: true, //table options as options to this hook
    enablePagination: false,
    manualPagination: true,
    enableTopToolbar: configureColumns ? true : false,
    enableToolbarInternalActions: configureColumns ? true : false,
    enableBottomToolbar: false,
    editDisplayMode: editingMode,
    enableEditing: enableEditing,
    enableColumnActions: false,
    columnFilterDisplayMode: columnFilterDisplayMode ?? 'popover',
    enableStickyHeader: true,
    manualFiltering: true,
    enableSelectAll: !disableDifferentDateRows ? true : false,
    enableColumnPinning: true,
    enableRowSelection: enableRowSelect
      ? (row): boolean => {
          return handleEnableRowSelection(row?.original);
        }
      : false,
    positionToolbarAlertBanner: 'none',
    onColumnFiltersChange: (filters) => {
      setColumnFilters(filters);
    },
    state: { sorting, rowSelection, isLoading: loader, columnFilters, columnVisibility, columnPinning },
    onColumnVisibilityChange: (visibilityState) => {
      setColumnVisibility(visibilityState);
    },
    renderTopToolbarCustomActions: () => (
      <ActionsColumn>
        {showSelectAllButton && (
          <SelectAllButtonDiv>
            <Button
              removeBgColor={theme.palette.secondary.main}
              type={ButtonType.Secondary}
              label={t('jobManagement:selectAll')}
              onClick={handleOpenSelectAll}
              disabled={loader || isAllRowsSelected}
            />
          </SelectAllButtonDiv>
        )}
        {enableRowSelect && selectedRows && selectedRows?.length > 0 ? (
          <RowSelectionCountDiv>
            <Typography>{`${selectedRows?.length} of ${totalCount} row(s) selected`}</Typography>
          </RowSelectionCountDiv>
        ) : null}
      </ActionsColumn>
    ),
    renderToolbarInternalActions: () => (
      <ButtonContainer>
        {type === JobManagementType.AllSchedules && (
          <IconButton
            size="large"
            onClick={() => {
              fetchData?.();
            }}
            disabled={loader}>
            <WhiteTooltip title={t('jobManagement:refresh')}>
              <img src={Icons.RefreshIcon} width={40} height={40} />
            </WhiteTooltip>
          </IconButton>
        )}
        <ModifyButtonContainer>
          {isMultipleRowsSelected && selectActions && (
            <DropdownWithButton
              selectedRows={selectedRows}
              type={type}
              setSelectedDropDownValue={setSelectedDropDownValue}
              selectedDropDownValue={selectedDropDownValue}
              filters={filters}
              processEvents={processEvents}
            />
          )}
        </ModifyButtonContainer>
        {isMultipleRowsSelected && selectActions && (
          <Button
            removeBgColor={theme.palette.secondary.main}
            type={ButtonType.Secondary}
            label={t('jobManagement:removeSelection')}
            onClick={handleRemoveSelection}
            disabled={loader}
          />
        )}
        <Button
          type={ButtonType.Primary}
          label={t('altTexts:configureColumns')}
          onClick={handleShowConfig}
          disabled={loader}
        />
      </ButtonContainer>
    ),
    renderBottomToolbarCustomActions: () => (
      <Box sx={{ border: '2px solid red' }}>
        <Button
          type={ButtonType.Primary}
          label={t('altTexts:configureColumns')}
          onClick={handleShowConfig}
          disabled={loader}
        />
      </Box>
    ),

    muiTableHeadCellProps: {
      sx: {
        border: `0.03125rem solid ${theme.palette.divider}`,
        padding: '0 0.75rem',
        paddingTop: 0,
        paddingBottom: 0,
        boxSizing: 'border-box',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.h4?.fontSize,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.primary,
        lineHeight: '1.5rem',
        letterSpacing: StyleConstants.LetterSpacingTight,
        height: '4.5rem',
        verticalAlign: 'middle',
        [theme.breakpoints.down('laptop')]: {
          fontSize: theme.typography.h5?.fontSize,
          lineHeight: '1.375rem',
          letterSpacing: '0.015625rem',
        },
        '& .MuiCheckbox-root:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'space-between',
          '& .MuiIconButton-root': {
            opacity: 1,
          },
          '& .MuiCheckbox-indeterminate': {
            color: theme.palette.primary.main,
          },
        },
      },
    },
    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        border: `0.03125rem solid ${theme.palette.divider}`,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.h4?.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: '1.5rem',
        letterSpacing: StyleConstants.LetterSpacingTight,
        color: theme.palette.text.secondary,
        height: '4rem',
        boxSizing: 'border-box',
        padding: '0 0.75rem',
        zIndex: column.getIsPinned() ? 1 : 0,
        [theme.breakpoints.down('laptop')]: {
          fontSize: theme.typography.h5?.fontSize,
          lineHeight: '1.375rem',
          letterSpacing: '0.015625rem',
          height: '3rem',
        },
        '& .MuiCheckbox-root:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
        '& .MuiCheckbox-root.Mui-disabled': {
          filter: 'opacity(0.3)',
          cursor: 'not-allowed',
        },
      },
    }),
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 320px)',
        '&::-webkit-scrollbar': { width: '12px', height: '12px' },
        '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '10px' },
        '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
      },
    },
    muiTableBodyRowProps: {
      hover: false,
      sx: {
        '&.Mui-selected': {
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },
          backgroundColor: theme.palette.secondary.dark,
        },
      },
    },
    muiSelectCheckboxProps: {
      checkedIcon: <img src={Icons.CheckedIcon} alt={t('altTexts:checked')} />,
      icon: <img src={Icons.Unchecked} alt={t('altTexts:unchecked')} />,
    },

    muiSelectAllCheckboxProps: {
      checkedIcon: <img src={Icons.CheckedIcon} alt={t('altTexts:checked')} />,
      icon: <img src={Icons.Unchecked} alt={t('altTexts:unchecked')} />,
    },
    muiSearchTextFieldProps: {
      placeholder: 'Search all users',
      sx: { minWidth: '300px' },
      variant: 'outlined',
    },

    muiFilterTextFieldProps: {
      sx: {
        color: 'red',
        '& .MuiInputBase-root': {
          borderRadius: '1rem',
          border: `0.0625rem solid ${theme.palette.primary.dark}`,
          '&:hover:not(.Mui-disabled, .Mui-error)': {
            '&::before': {
              borderBottom: 'none',
            },
          },
          '&::before,&::after': {
            borderBottom: 'none',
          },
        },
        '& .MuiInputBase-input': {
          paddingLeft: '0.625rem',
          '&::placeholder': {
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.h4?.fontSize,
            fontWeight: theme.typography.fontWeightRegular,
            lineHeight: '1.5rem',
            letterSpacing: StyleConstants.LetterSpacingTight,
            color: theme.palette.text.secondary,
          },
        },
        '& .MuiIconButton-root': {
          color: theme.palette.primary.dark,
          marginRight: '0.25rem',
          '&.Mui-disabled': {
            color: theme.palette.primary.dark,
          },
        },
      },
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        }
      },
    },
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTopToolbarProps: {
      sx: {
        background: theme.palette.secondary.main,
        minHeight: '5.5rem',
        [theme.breakpoints.down('laptop')]: {
          minHeight: '5rem',
        },
        '& > div': {
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    sortDescFirst: false,
    onSortingChange: (ar) => {
      setSorting(ar);
    },
    muiCircularProgressProps: {
      sx: {
        color: theme.palette.primary.dark,
      },
    },
    onRowSelectionChange: handleRowSelectionChange,
    getRowId: (originalRow) =>
      type === JobManagementType.AllSchedules
        ? isRescheduleShift
          ? (originalRow.workOrderScheduleId as string)
          : (originalRow.woScheduleMasterId as string)
        : (originalRow.jobId as string),
    icons: CustomIcons,
    enableFilterMatchHighlighting: false,
  });

  const handleToggleAll = (visible: boolean) => {
    table?.getAllColumns()?.forEach((column) => {
      if (column?.getCanHide()) {
        column?.toggleVisibility(visible);
      }
    });
  };

  const handleClose = useCallback(() => {
    setShowConfig(false);
  }, []);

  const showAllColumns = useCallback(() => {
    handleToggleAll(true);
  }, []);

  const hideAllColumns = useCallback(() => {
    handleToggleAll(false);
  }, []);

  const allColumnsHidden = useMemo(
    () =>
      table
        ?.getAllColumns()
        ?.filter((col) => col.id !== 'mrt-row-select')
        ?.every((col) => !col?.getIsVisible()),
    [table?.getAllColumns()]
  );

  const allColumnsVisible = useMemo(
    () => table?.getAllColumns()?.every((col) => col?.getIsVisible()),
    [table?.getAllColumns()]
  );

  useEffect(() => {
    if (removeSelectedJobs && unSelectJobIds && unSelectJobIds?.length > 0) {
      dispatch(removeSelectedJobsState(unSelectJobIds));
    }
  }, [rowSelection, unSelectJobIds, removeSelectedJobs]);

  useEffect(() => {
    // rowSelection having current selected state value through that we triggered onRowChange
    if (onRowChange && Object.keys(rowSelection)?.length) {
      onRowChange(rowSelection);
    }
  }, [rowSelection]);

  useEffect(() => {
    if (hasMounted.current) {
      filter?.(columnFilters);
    } else {
      hasMounted.current = true;
    }
  }, [columnFilters]);

  useEffect(() => {
    if (sorting?.length) {
      sort?.(sorting);
    }
  }, [sorting]);

  useEffect(() => {
    if (resetSelectionSignal) {
      handleRemoveSelection();
    }
  }, [resetSelectionSignal]);

  useEffect(() => {
    const selectedRowsLength = Object.keys(rowSelection).length;
    const areAllRowsSelected = totalCount === selectedRowsLength;
    setIsAllRowsSelected(areAllRowsSelected);
  }, [rowSelection, totalCount]);

  useEffect(() => {
    if (!isEmpty(rowSelectionState) && !isEmpty(selectedRowDataMapState) && storeRowSelection) {
      setRowSelection(rowSelectionState);

      const updatedDataMap: Record<string, any> = {};
      data.forEach((row) => {
        const rowId =
          type === JobManagementType.AllSchedules
            ? isRescheduleShift
              ? row.workOrderScheduleId
              : row.woScheduleMasterId
            : row.jobId;

        if (rowSelectionState[rowId as string]) {
          updatedDataMap[rowId as string] = row;
        }
      });

      setSelectedRowDataMap(updatedDataMap);
    }
  }, [rowSelectionState, selectedRowDataMapState, data]);

  useEffect(() => {
    const selectedNewRows = Object.values(selectedRowDataMap);

    if (selectedNewRows) {
      setSelectedRows?.(selectedNewRows);
    }
  }, [rowSelection, removeSelectedJobs, selectedRowDataMap]);

  useEffect(() => {
    if (enableColumnPinning) {
      setColumnPinning({ left: ['mrt-row-select', 'jobNumber', 'jobName'] });
    }
  }, [enableColumnPinning]);

  return (
    <>
      <Modal open={showConfig} onClose={handleClose}>
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
            padding: '1.5rem 0rem',
            maxWidth: '18.75rem',
            margin: 'auto',
            marginTop: '10%',
            borderRadius: '1rem',
            boxShadow: 24,
            maxHeight: '80vh',
            overflowY: 'auto',
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              padding: '0rem 1.5rem',
            }}>
            <Typography
              style={{
                ...columnsVisible,
                opacity: allColumnsHidden ? 0.5 : 1,
              }}
              onClick={hideAllColumns}>
              {t('altTexts:hideAll')}
            </Typography>

            <Typography
              onClick={showAllColumns}
              style={{
                ...columnsVisible,
                opacity: allColumnsVisible ? 0.5 : 1,
              }}>
              {t('altTexts:showAll')}
            </Typography>
          </Box>
          <Divider sx={{ marginBottom: '1rem' }} />

          <Box sx={{ maxHeight: '45vh', overflowY: 'auto', marginBottom: '1rem' }}>
            <List>
              {table
                .getAllColumns()
                .filter((column) => column.id !== 'mrt-row-select')
                .map((column) => (
                  <MenuItem
                    key={column.id}
                    value={column.id}
                    sx={{
                      maxWidth: '22.5rem',
                      background: column.getIsVisible() ? theme.palette.warning.contrastText : 'white',

                      '&:hover': {
                        background: column.getIsVisible() ? theme.palette.warning.contrastText : 'white',
                      },
                    }}
                    onClick={column.getToggleVisibilityHandler()}>
                    {column.getIsVisible() ? (
                      <img src={Icons.CheckedIcon} alt={t('altTexts:checked')} style={{ ...checkBox }} />
                    ) : (
                      <img src={Icons.Unchecked} alt={t('altTexts:unchecked')} style={{ ...checkBox }} />
                    )}
                    <ListItemText primary={column.columnDef.header} sx={{ ...listItemStyles }} />
                  </MenuItem>
                ))}
            </List>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Button type={ButtonType.Secondary} label={t('altTexts:cancel')} onClick={handleClose} />
          </Box>
        </Box>
      </Modal>

      <Box sx={{ position: 'relative' }}>
        <Box style={{ position: 'relative', opacity: loader ? 0.5 : 1 }}>
          <MaterialReactTable table={table} />
        </Box>
      </Box>
      <ModifyPopUp
        open={isOpenSelectAll}
        handleClose={handleCloseSelectAll}
        handleOnCloseJob={handleSelectAll}
        heading={''}
        text={t('jobManagement:selectAllPopupText')}
        loading={isLoadingSelectAll}
      />
    </>
  );
};

DataGrid.defaultProps = {
  enableRowSelect: true,
  enableColumnPinning: true,
  leftPinArr: '',
  rightPinArr: 'Action',
  enableEditing: false,
  editingMode: 'table',
  enableHiding: true,
  enableCellActions: true,
};

export default DataGrid;
